import React, { useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Col, Row, Space, Form, Input, Tabs, Card, Select, Typography } from 'antd';
import { GoogleApiWrapper } from 'google-maps-react';

import CampaignRequestorSummary from './CampaignRequestorSummaryOptimized';
import CampaignRequestorDetails from './CampaignRequestorDetails';
import CampaignRequestorBudget from './CampaignRequestorBudget';
import CampaignRequestorCategories from './CampaignRequestorCategories';
import CampaignRequestorLineItem from './CampaignRequestorLineItem';
import CampaignRequestorNotes from './CampaignRequestorNotes';
import CampaignRequestorAttributionReporting from './CampaignRequestorAttributionReporting';
import { CampaignOrderQueueHistory } from '../CampaignOrderQueue/components/CampaignOrderQueueHistory';
import { CampaignOrderQueueProvider } from '../../../core/components/campaigns/campaignordersqueue/CampaingOrderQueueProvider';
import OverlaySpinner from '../../shared/OverlaySpinner';
import { useCampaignForm } from '../../../core/hooks/useCampaignForm';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { campaignTypeMap, defaultIabCategory } from '../../../core/utils/constants/constants';
import googleMapsConfig from '../../../platform/shared/maps/googlemaps';
import { CampaignConfig } from '../Campaigns/shared/configs/campaignConfig';
import { handleInitialValues } from './utils/formInitialization';

const { Text } = Typography;

const CampaignRequestorEdit = ({ google }) => {
  const history = useHistory();
  const { id } = useParams();
  const mode = 'edit'; // Fixed to edit mode
  const cpmInitializedRef = useRef(false);

  const {
    form,
    formValues,
    derivedState,
    validationState,
    helpers
  } = useCampaignForm(mode);

  const {
    selectedAdvertiser,
    setSelectedAdvertiser,
    childrenOrg,
    fetchCampaignOrder,
    currentCampaignOrder,
    loadingCampaignOrder,
    currentUserOrg,
    fetchSelectedAdvertiser,
  } = useCampaignRequestorContext();

  // Initialize form with campaign data when loading existing campaign
  useEffect(() => {
    if (id) {
      fetchCampaignOrder({
        variables: {
          where: { id },
        },
      });
    }
  }, [id, fetchCampaignOrder]);

  // Handle form initialization when campaign data is loaded
  useEffect(() => {
    if (currentCampaignOrder) {
      handleInitialValues(currentCampaignOrder, form, history);

      // Fetch advertiser data if different from selected
      if (
        currentCampaignOrder.advertiser &&
        selectedAdvertiser &&
        currentCampaignOrder.advertiser.id !== selectedAdvertiser.id
      ) {
        fetchSelectedAdvertiser({
          variables: {
            where: {
              id: currentCampaignOrder.advertiser.id,
            },
          },
        });
      }
    } else if (!id) {
      // Initialize default values for new campaigns
      const moment = require('moment');
      form.setFieldsValue({
        startDate: moment(),
        endDate: moment().add(2, 'days'),
        cpm: 7,
        campaigns: CampaignConfig.lineItemsDefaultValue,
      });

      // Trigger initial validation to show required field errors
      setTimeout(() => {
        form.validateFields().catch(() => {
          // Ignore validation errors, we just want to show the error states
        });
      }, 100);
    }
  }, [currentCampaignOrder, currentUserOrg, form, history, fetchSelectedAdvertiser, selectedAdvertiser, id]);

  // Handle advertiser selection changes
  useEffect(() => {
    if (selectedAdvertiser && childrenOrg && childrenOrg.length) {
      const matchedAdvertiser = childrenOrg.find((org) => org.id === selectedAdvertiser.id);

      if (matchedAdvertiser) {
        const cpmNum = currentCampaignOrder
          ? currentCampaignOrder.cpm
          : Number(matchedAdvertiser.cpm.replace(/,/g, ''));

        // Only set CPM on initial load, not on every render
        if (!cpmInitializedRef.current) {
          helpers.setFieldsValue({ cpm: cpmNum });
          cpmInitializedRef.current = true;
        }

        const campaigns = helpers.getFieldValue('campaigns');
        if (campaigns) {
          const currentCpm = helpers.getFieldValue('cpm');
          helpers.setFieldsValue({
            impressions: derivedState.impressions,
            campaigns: campaigns.map((campaign) => ({
              ...campaign,
              cpm: currentCpm || cpmNum,
              impressions: derivedState.campaigns.find(c => c.id === campaign.id)?.impressions || campaign.impressions,
            })),
          });
        }

        setSelectedAdvertiser(matchedAdvertiser);
      }
    }
  }, [selectedAdvertiser, childrenOrg, currentCampaignOrder, setSelectedAdvertiser, helpers, derivedState]);

  if (loadingCampaignOrder) {
    return <OverlaySpinner />;
  }

  return (
    <div style={{ margin: 20 }}>
      <Form
        form={form}
        onValuesChange={helpers.handleValuesChange}
        validateTrigger={['onChange', 'onBlur']}
        initialValues={{
          name: '',
          description: '',
          startDate: undefined,
          endDate: undefined,
          numberOfDays: 2,
          type: 1,
          renewalStatus: 'EXPECTED_TO_RENEW',
          advertiserId: selectedAdvertiser ? selectedAdvertiser.id : null,
          budget: 0,
          cpm: 7,
          isRegulated: true,
          category: defaultIabCategory.code,
          attributionTargets: [],
          geoTargets: [],
          measureWalkInTraffic: false,
          advertiserType: 'LOCAL_DELIVERY_SERVICE',
          transactionTarget: null,
          transactionsConfig: null,
          signUpConfig: null,
          awarenessCampaign: false,
          sessionConfig: null,
          notes: null,
          IONumber: null,
          productCode: 'DIS',
          campaigns: CampaignConfig.lineItemsDefaultValue,
        }}
      >
        <Row gutter={20}>
          <Col span={6}>
            <CampaignRequestorSummary
              formValues={formValues}
              derivedState={derivedState}
              validationState={validationState}
              helpers={helpers}
              mode={mode}
              handleInitialValues={(campaignOrder) =>
                handleInitialValues(campaignOrder, form, history)
              }
            />
          </Col>
          <Col span={18}>
            <Tabs defaultActiveKey="form">
              <Tabs.TabPane tab="Form" key="form">
                <Row>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <CampaignRequestorDetails
                      formValues={formValues}
                      helpers={helpers}
                    />
                    <CampaignRequestorBudget
                      formValues={formValues}
                      derivedState={derivedState}
                      helpers={helpers}
                    />
                    <CampaignRequestorCategories
                      formValues={formValues}
                      helpers={helpers}
                    />
                    <CampaignRequestorAttributionReporting
                      formValues={formValues}
                      helpers={helpers}
                      google={google}
                    />
                    <Card>
                      <Row>
                        <Col span={8}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>Campaign Type</Text>
                            <Text type="secondary">
                              Media format of ad units for this campaign
                            </Text>
                            <Form.Item name="type">
                              <Select
                                style={{ width: '100%' }}
                                showSearch
                                placeholder="Display Campaign"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                options={[
                                  { label: 'Display', value: 1, key: 1 },
                                  { label: 'External', value: 7, key: 7 },
                                  { label: 'Video', value: 8, key: 8 },
                                ]}
                              />
                            </Form.Item>
                          </Space>
                        </Col>
                      </Row>
                      <Form.List name="campaigns">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {fields.map(({ key, name, fieldKey, ...restField }, index) => (
                              <CampaignRequestorLineItem
                                key={key}
                                formValues={formValues}
                                derivedState={derivedState}
                                helpers={helpers}
                                restField={restField}
                                name={name}
                                fieldKey={fieldKey}
                                index={index}
                                add={add}
                                remove={remove}
                                currentCampaignOrder={currentCampaignOrder}
                              />
                            ))}
                          </Space>
                        )}
                      </Form.List>
                    </Card>
                    <CampaignRequestorNotes
                      formValues={formValues}
                      helpers={helpers}
                    />
                  </Space>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane tab="History" key="history" disabled={!currentCampaignOrder}>
                <CampaignOrderQueueProvider>
                  <CampaignOrderQueueHistory currentCampaignOrder={currentCampaignOrder} />
                </CampaignOrderQueueProvider>
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
        <Form.Item name="geoTargets" hidden>
          <Input />
        </Form.Item>
      </Form>
    </div>
  );
};

export default GoogleApiWrapper(googleMapsConfig)(CampaignRequestorEdit);